import { getAppConfigSync, getAppConfig } from "../utils/getAppConfig";
import { getProductsForInventory } from "../Components/globalFunctions";
import store from "../Redux";

// Track which hub's products we've already fetched to avoid
// calling `inventory_products/` multiple times for the same hub.
let lastProductsHubId = null;

/**
 * Normalize hex color to ensure it has a # prefix
 * @param {string} color - Color value (with or without #)
 * @param {string} fallback - Fallback color if color is invalid
 * @returns {string} Normalized color with # prefix
 */
function normalizeHexColor(color, fallback = "#000000") {
  if (!color || typeof color !== 'string') {
    return fallback;
  }
  // Remove any whitespace
  color = color.trim();
  // If it already starts with #, return as is
  if (color.startsWith('#')) {
    return color;
  }
  // Otherwise, add # prefix
  return `#${color}`;
}

/**
 * Get the brand configuration dynamically from backend AppConfig
 * @returns {Object} Brand configuration object with name, theme, images, footer, etc.
 */
export default function getBrand() {
  // Try to get AppConfig synchronously (from cache)
  let appConfig = getAppConfigSync();
  // Helper to always get the latest AppConfig from cache (if available)
  const getCurrentAppConfig = () => {
    const latest = getAppConfigSync();
    if (latest) {
      appConfig = latest;
      return latest;
    }
    return appConfig;
  };
  
  // If not in cache, trigger async fetch (but return a proxy object that will use defaults until loaded)
  if (!appConfig) {
    // Trigger async fetch in background
    getAppConfig().catch(err => console.error("Error fetching AppConfig:", err));
  }

  // Create a dynamic brand object that uses AppConfig when available, with fallbacks
  const brand = {};
  
  Object.defineProperties(brand, {
    name: {
      get() {
        const cfg = getCurrentAppConfig();
        const hub = localStorage.getItem("selected_hub");
        try {
          const parsed = JSON.parse(hub);
          if (parsed?.place) return parsed.place;
        } catch (e) {
          // Not JSON
        }
        return cfg?.inventory?.place || "Hub";
      },
    },
    appTitle: {
      get() {
        const user_role = typeof window !== 'undefined' ? localStorage.getItem('role') : null;
        if (user_role === 'super_admin') {
          return "KOODAI";
        }
        const cfg = getCurrentAppConfig();
        return cfg?.app_title || "SmartKart Control Hub";
      },
    },
    appHeading: {
      get() {
        const cfg = getCurrentAppConfig();
        return cfg?.app_heading || cfg?.header_title || "Control Hub";
      },
    },
    appIcon: {
      get() {
        const cfg = getCurrentAppConfig();
        return cfg?.app_icon_url || cfg?.brand_icon_url || "/logo192.png";
      },
    },
    favicon: {
      get() {
        const cfg = getCurrentAppConfig();
        return cfg?.favicon_url || "/logo192.png";
      },
    },
    appShortName: {
      get() {
        const user_role = typeof window !== 'undefined' ? localStorage.getItem('role') : null;
        if (user_role === 'super_admin') {
          return "KOODAI";
        }
        const cfg = getCurrentAppConfig();
        return cfg?.app_short_name || "SmartKart";
      },
    },
    appDescription: {
      get() {
        const cfg = getCurrentAppConfig();
        return cfg?.app_description || "SmartKart Control Hub - Manage your inventory and orders";
      },
    },
    headerTitle: {
      get() {
        const user_role = typeof window !== 'undefined' ? localStorage.getItem('role') : null;
        if (user_role === 'super_admin') {
          return "KOODAI";
        }
        const cfg = getCurrentAppConfig();
        return cfg?.header_title || "Control Hub";
      },
    },
    headerSubtitle: {
      get() {
        const user_role = typeof window !== 'undefined' ? localStorage.getItem('role') : null;
        if (user_role === 'super_admin') {
          return "KOODAI";
        }
        const cfg = getCurrentAppConfig();
        return cfg?.header_subtitle || "SmartKart";
      },
    },
    theme: {
      get() {
        const cfg = getCurrentAppConfig();
        // Use theme_colors from AppConfig if available, otherwise build from theme_color and background_color
        if (cfg?.theme_colors && typeof cfg.theme_colors === "object") {
          return {
            background: normalizeHexColor(cfg.theme_colors.background || cfg.background_color, "#ffffff"),
            text: normalizeHexColor(cfg.theme_colors.text, "#000000"),
            footerText: normalizeHexColor(cfg.theme_colors.textSecondary, "#6B7280"),
            primary: normalizeHexColor(cfg.theme_colors.primary || cfg.theme_color, "#1976d2"),
            primaryHover: normalizeHexColor(cfg.theme_colors.primaryHover, "#115293"),
            textPrimary: normalizeHexColor(cfg.theme_colors.textPrimary, "#000000"),
            textSecondary: normalizeHexColor(cfg.theme_colors.textSecondary, "#555555"),
            selected: normalizeHexColor(cfg.theme_colors.selected, "#e0f2ff"),
            hoverBackground: normalizeHexColor(cfg.theme_colors.hoverBackground, "#f5f5f5"),
            border: normalizeHexColor(cfg.theme_colors.border, "#e0e0e0"),
            chipSelected: normalizeHexColor(cfg.theme_colors.chipSelected || cfg.theme_color, "#1976d2"),
          };
        }
        
        // Fallback: build theme from basic colors
        return {
          background: normalizeHexColor(cfg?.background_color, "#ffffff"),
          text: "#000000",
          footerText: "#6B7280",
          primary: normalizeHexColor(cfg?.theme_color, "#1976d2"),
          primaryHover: "#115293",
          textPrimary: "#000000",
          textSecondary: "#555555",
          selected: "#e0f2ff",
          hoverBackground: "#f5f5f5",
          border: "#e0e0e0",
          chipSelected: normalizeHexColor(cfg?.theme_color, "#1976d2"),
        };
      },
    },
    products: {
      get() {
        const hub = localStorage.getItem("selected_hub");
        let hubId = null;

        try {
          if (hub) {
            const parsed = JSON.parse(hub);
            hubId = Array.isArray(parsed)
              ? parsed[0]
              : (typeof parsed === "object" ? parsed.id || parsed.value : parsed);
          }
        } catch (e) {
          const parsedInt = parseInt(hub, 10);
          if (!isNaN(parsedInt)) {
            hubId = parsedInt;
          }
        }


        // Only trigger a products fetch when the hub actually changes.
        if (hubId && hubId !== lastProductsHubId) {
          lastProductsHubId = hubId;
          getProductsForInventory(hubId);
        } else if (!hubId) {
          console.warn("[brand.products] No hubId resolved from selected_hub, products will be empty");
        }

        const products = store.getState().products.products;
        return products;
      },
    },
    footer: {
      get() {
        const cfg = getCurrentAppConfig();
        // Return footer_text from AppConfig if available
        if (cfg?.footer_text) {
          return cfg.footer_text;
        }
        // Fallback to hub name if footer_text is not set
        const hub = localStorage.getItem("selected_hub");
        if (!hub) return "";
        try {
          const parsed = JSON.parse(hub);
          if (parsed?.place) return parsed.place;
        } catch (e) {
          // Not JSON
        }
        return cfg?.inventory?.place || "";
      },
    },
    // Additional properties for PWA manifest
    themeColor: {
      get() {
        const cfg = getCurrentAppConfig();
        return normalizeHexColor(cfg?.theme_color, "#000000");
      },
    },
    backgroundColor: {
      get() {
        const cfg = getCurrentAppConfig();
        return normalizeHexColor(cfg?.background_color, "#ffffff");
      },
    },
    displayMode: {
      get() {
        const cfg = getCurrentAppConfig();
        return cfg?.display_mode || "standalone";
      },
    },
    appIcon192: {
      get() {
        const cfg = getCurrentAppConfig();
        return cfg?.app_icon_192_url || cfg?.app_icon_url || "/logo192.png";
      },
    },
    appIcon512: {
      get() {
        const cfg = getCurrentAppConfig();
        return cfg?.app_icon_512_url || cfg?.app_icon_url || "/logo512.png";
      },
    },
    appleTouchIcon: {
      get() {
        const cfg = getCurrentAppConfig();
        return cfg?.apple_touch_icon_url || cfg?.app_icon_url || "/logo192.png";
      },
    },
  });

  return brand;
}

